/**
 * Today (slice 4b spec §5, Paper "Today · routines quiet", board C): what the
 * crew is working on for you, your recent chats and docs in one list, and the
 * ask box, pinned at the bottom. Routines stay out of the list: on a wide
 * screen what ran and the week's next runs are in a rail with the owner's
 * line, on a phone one line. Home again (spec §6).
 */
import { FULL_HEIGHT_PAGE } from "@crew/ui/components/family-shell";
import { Tabs, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { TextLink } from "@crew/ui/components/typography";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { useEffect, useState } from "react";
import type { Me } from "../../app/me";
import { useNow } from "../../app/now";
import { usePanel } from "../chats/panel-state";
import { InstallCard } from "../chats/install-card";
import { StartComposer } from "../chats/start-composer";
import { ComputerCard } from "../computer/computer-card";
import { useToday, useTodaySeen, type Today } from "./api";
import { Check } from "lucide-react";
import { RanEntry, RecentRow, WeekEntry, WorkingCard } from "./parts";
import {
  FILTERS,
  FILTER_LABELS,
  emptyLine,
  filterRecent,
  greeting,
  othersLine,
  ranSummary,
  shortDate,
  sinceLabel,
  type Filter,
} from "./today-view";

/** After 3 seconds in view (a visible tab), "since" starts from now next time (spec §3.2). */
function useSeenAfterView(ready: boolean) {
  const { mutate } = useTodaySeen();
  useEffect(() => {
    if (!ready) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const watch = () => {
      clearTimeout(timer);
      if (document.visibilityState === "visible") timer = setTimeout(() => mutate(), 3000);
    };
    watch();
    document.addEventListener("visibilitychange", watch);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", watch);
    };
  }, [ready, mutate]);
}

const label = "text-2xs font-semibold tracking-[0.06em] text-muted-foreground uppercase";

function Header({ me, today, now }: { me: Me; today: Today | undefined; now: Date }) {
  const tz = me.workspace.timezone;
  const since = today ? sinceLabel(today, tz, now) : null;
  return (
    <header className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-4">
      <h1 className="font-display text-[28px] leading-8 tracking-tight text-foreground md:text-title">
        {greeting(me.user.name, tz, now)}
      </h1>
      <p className={cn("text-meta text-muted-foreground", !since && "hidden md:block")}>
        <span className="hidden md:inline">
          {shortDate(now, tz)}
          {since ? " · " : ""}
        </span>
        {since ? (
          <span className="first-letter:uppercase md:first-letter:lowercase">{since}</span>
        ) : null}
      </p>
    </header>
  );
}

function RecentList({ today, now }: { today: Today; now: Date }) {
  const [filter, setFilter] = useState<Filter>("all");
  const items = filterRecent(today.recent, filter);
  return (
    <section aria-label="Recent" className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <h2 className={label}>Recent</h2>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <TabsList aria-label="Show" className="h-8">
            {FILTERS.map((f) => (
              <TabsTrigger key={f} value={f} data-filter={f} className="px-2.5 text-meta">
                {FILTER_LABELS[f]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      {items.length === 0 ? (
        <p className="border-t border-border py-4 text-sm text-muted-foreground">
          {emptyLine(filter)}
        </p>
      ) : (
        <ul className="flex flex-col border-t border-border">
          {items.map((item) => (
            <RecentRow key={`${item.kind}:${item.id}`} item={item} tz={today.timezone} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Under the rail's width, Ran is one line (spec §5): "3 routines ran this week", and Routines. */
function RanLine({ today }: { today: Today }) {
  const summary = ranSummary(today.ran_count);
  if (!summary) return null;
  return (
    <p data-ran-line className="flex items-center gap-2 text-meta text-muted-foreground xl:hidden">
      <Check className="size-4 shrink-0 text-moss" strokeWidth={2.4} aria-hidden />
      <span className="min-w-0 flex-1">{summary}</span>
      <TextLink href="/routines" className="shrink-0 font-medium text-foreground">
        Routines
      </TextLink>
    </p>
  );
}

/**
 * The rail (1280px and up): what ran this week and the week's next runs,
 * then the owner's line per person waiting.
 */
function Rail({ today, now }: { today: Today; now: Date }) {
  const others = today.others_waiting ?? [];
  return (
    <aside
      aria-label="Routines"
      className="hidden w-[300px] shrink-0 flex-col gap-8 overflow-y-auto border-l border-border px-6 py-11 xl:flex"
    >
      {today.ran.length > 0 ? (
        <section className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className={label}>Ran this week</h2>
            <TextLink href="/routines" className="text-meta">
              Routines
            </TextLink>
          </div>
          <ul className="flex flex-col">
            {today.ran.map((r) => (
              <RanEntry key={r.id} item={r} tz={today.timezone} now={now} />
            ))}
          </ul>
        </section>
      ) : null}
      <section className="flex flex-col gap-2">
        <h2 className={label}>Next this week</h2>
        {today.week.length === 0 ? (
          <p className="text-sm text-muted-foreground">No routines run this week.</p>
        ) : (
          <ul className="flex flex-col">
            {today.week.map((w) => (
              <WeekEntry key={w.routine.id} item={w} tz={today.timezone} now={now} />
            ))}
          </ul>
        )}
      </section>
      {others.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className={label}>The crew</h2>
          {others.map((o) => (
            <p key={o.user.id} className="text-sm text-copy">
              {othersLine(o, today.timezone, now)}{" "}
              <TextLink href="/tasks?filter=waiting">Tasks</TextLink>
            </p>
          ))}
        </section>
      ) : null}
    </aside>
  );
}

export function TodayPage({ me }: { me: Me }) {
  const [, setPanel] = usePanel();
  const phone = useIsMobile();
  const { data: today } = useToday();
  const now = new Date(useNow());
  useSeenAfterView(today !== undefined);
  return (
    <div className={FULL_HEIGHT_PAGE} data-today>
      <main className="flex min-w-0 flex-1 justify-center">
        <div className="flex w-full max-w-[720px] min-h-0 flex-col">
          {/* The list scrolls; the ask box stays put under it. */}
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-5 pb-6 md:px-6 md:pt-10">
            <Header me={me} today={today} now={now} />
            {today ? <RanLine today={today} /> : null}
            <ComputerCard me={me} />
            <InstallCard />
            {today?.working.map((w) => (
              <WorkingCard key={w.turn_id} w={w} now={now} />
            ))}
            {today ? <RecentList today={today} now={now} /> : null}
          </div>
          <div className="flex shrink-0 flex-col gap-2.5 border-t border-border bg-background px-3 pt-2.5 pb-3 md:border-t-0 md:px-6 md:pb-7">
            {/* A phone's is one line, so the list keeps the screen (spec §5). */}
            <StartComposer
              size={phone ? "md" : "lg"}
              label="Ask the crew"
              placeholder={phone ? "Ask the crew for something" : "Ask the crew for something."}
              onStarted={setPanel}
            />
          </div>
        </div>
      </main>
      {today ? <Rail today={today} now={now} /> : null}
    </div>
  );
}
